import React, { useState, useEffect, useReducer, useRef } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { Search, Plus, Edit2, Trash2, Zap, Download, Upload, Loader2 } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QuickAnswersModal from "../../components/QuickAnswersModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { parseCsv, toCsv } from "../../helpers/quickAnswersFile";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICK_ANSWERS") {
    const qs = action.payload;
    const newQs = [];
    qs.forEach((q) => {
      const idx = state.findIndex((x) => x.id === q.id);
      if (idx !== -1) state[idx] = q; else newQs.push(q);
    });
    return [...state, ...newQs];
  }
  if (action.type === "UPDATE_QUICK_ANSWERS") {
    const q = action.payload;
    const idx = state.findIndex((x) => x.id === q.id);
    if (idx !== -1) { state[idx] = q; return [...state]; }
    return [q, ...state];
  }
  if (action.type === "DELETE_QUICK_ANSWERS") return state.filter((q) => q.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

const QuickAnswers = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [quickAnswers, dispatch] = useReducer(reducer, []);
  const [selectedQA, setSelectedQA] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingQA, setDeletingQA] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/quickAnswers/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_QUICK_ANSWERS", payload: data.quickAnswers });
        setHasMore(data.hasMore);
      } catch (err) { toastError(err); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    const handleQuickAnswer = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_QUICK_ANSWERS", payload: data.quickAnswer });
      if (data.action === "delete")
        dispatch({ type: "DELETE_QUICK_ANSWERS", payload: +data.quickAnswerId });
    };

    socket.on("quickAnswer", handleQuickAnswer);

    return () => {
      socket.off("quickAnswer", handleQuickAnswer);
    };
  }, []);

  const handleDeleteQA = async (id) => {
    try {
      await api.delete(`/quickAnswers/${id}`);
      toast.success(i18n.t("quickAnswers.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingQA(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const reloadList = () => {
    dispatch({ type: "RESET" });
    setHasMore(false);
    if (pageNumber === 1 && searchParam === "") {
      // Mismo estado de búsqueda: el efecto de carga no se dispara solo.
      api
        .get("/quickAnswers/", { params: { searchParam: "", pageNumber: 1 } })
        .then(({ data }) => {
          dispatch({ type: "LOAD_QUICK_ANSWERS", payload: data.quickAnswers });
          setHasMore(data.hasMore);
        })
        .catch(toastError);
    } else {
      setSearchParam("");
      setPageNumber(1);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/quickAnswers/export");
      if (!data.quickAnswers.length) {
        toast.info("No hay respuestas rápidas para exportar.");
        return;
      }
      const blob = new Blob([toCsv(data.quickAnswers)], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "respuestas-rapidas.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) { toastError(err); }
    setExporting(false);
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const items = parseCsv(await file.text());
      const valid = items.filter((i) => i.shortcut && i.message);
      if (!valid.length) {
        toast.error("El archivo no trae respuestas válidas. Usa dos columnas: atajo y mensaje.");
        return;
      }
      setOverwrite(false);
      setImportPreview({ items, valid: valid.length, invalid: items.length - valid.length });
    } catch (err) { toastError(err); }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/quickAnswers/import", {
        items: importPreview.items,
        overwrite,
      });
      const parts = [`${data.created} nueva(s)`];
      if (data.updated) parts.push(`${data.updated} actualizada(s)`);
      if (data.skipped) parts.push(`${data.skipped} omitida(s) por existir`);
      if (data.invalid) parts.push(`${data.invalid} inválida(s)`);
      toast.success(`Importación lista: ${parts.join(", ")}.`);
      setImportPreview(null);
      reloadList();
    } catch (err) { toastError(err); }
    setImporting(false);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingQA ? `${i18n.t("quickAnswers.confirmationModal.deleteTitle")} /${deletingQA.shortcut}?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => handleDeleteQA(deletingQA.id)}
      >
        {i18n.t("quickAnswers.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickAnswersModal
        open={modalOpen}
        onClose={() => { setSelectedQA(null); setModalOpen(false); }}
        quickAnswerId={selectedQA?.id}
      />

      {importPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Importar respuestas rápidas</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              El archivo trae <strong>{importPreview.valid}</strong> respuesta(s) válida(s)
              {importPreview.invalid > 0 && <> y {importPreview.invalid} fila(s) incompleta(s) que se ignorarán</>}.
            </p>
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="mt-0.5" />
              <span>
                Reemplazar el mensaje de las que ya existen con el mismo atajo.
                <span className="block text-xs text-slate-400">Si no lo marcas, esas se omiten y no se pierde nada.</span>
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setImportPreview(null)} disabled={importing} className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
              >
                {importing && <Loader2 className="w-4 h-4 animate-spin" />}
                Importar
              </button>
            </div>
          </div>
        </div>
      )}

      <MainHeader>
        <Title>{i18n.t("quickAnswers.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              placeholder={i18n.t("quickAnswers.searchPlaceholder")}
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
              className="pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-52 transition-all"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileChosen}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Importar desde un archivo CSV (atajo, mensaje)"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Importar
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            title="Descargar todas las respuestas en un archivo CSV"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Exportar
          </button>
          <button
            onClick={() => { setSelectedQA(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            {i18n.t("quickAnswers.buttons.add")}
          </button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6" onScroll={handleScroll}>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[550px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{i18n.t("quickAnswers.table.shortcut")}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{i18n.t("quickAnswers.table.message")}</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">{i18n.t("quickAnswers.table.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {quickAnswers.length === 0 && !loading ? (
                <tr>
                  <td colSpan={3} className="py-16 text-center">
                    <Zap className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">No hay respuestas rápidas</p>
                    <p className="text-sm text-slate-400 mt-1">Crea respuestas para agilizar tus conversaciones</p>
                  </td>
                </tr>
              ) : (
                quickAnswers.map((qa) => (
                  <tr key={qa.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-mono font-bold">
                        <Zap className="w-3 h-3" />/{qa.shortcut}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="text-slate-600 dark:text-slate-400 text-sm truncate">{qa.message}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setSelectedQA(qa); setModalOpen(true); }} title="Editar" className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setConfirmOpen(true); setDeletingQA(qa); }} title="Eliminar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {loading && (
                <tr><td colSpan={3} className="py-4 text-center"><div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default QuickAnswers;
